import { useMemo, useState } from 'react'
import { Badge } from '../Badge'
import { Card } from '../Card'
import { useToast } from '../Toast'
import { useDb } from '../../state/DbProvider'
import { findUser, getCurrentUser } from '../../state/selectors'
import type { AgencySignupRequest } from '../../state/types'
import { formatDateTimeJP } from '../../utils/format'

const actionButtonClass =
  'rounded-lg border px-3 py-2 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50'

function agencySignupStatusTone(status: AgencySignupRequest['status']) {
  if (status === '審査待ち') return 'amber'
  if (status === '承認') return 'green'
  return 'slate'
}

function formatDateTimeOrDash(value?: string) {
  return value ? formatDateTimeJP(value) : '-'
}

export function AgencySignupApprovalCard() {
  const { db, actions } = useDb()
  const toast = useToast()
  const [activeRequestAction, setActiveRequestAction] = useState<string | null>(null)
  const [showPendingRequestsOnly, setShowPendingRequestsOnly] = useState(true)
  const me = db ? getCurrentUser(db) : null
  const agencySignupRequests = db?.agencySignupRequests ?? []

  const pendingAgencySignupRequestCount = useMemo(
    () => agencySignupRequests.filter((request) => request.status === '審査待ち').length,
    [agencySignupRequests],
  )
  const orderedAgencySignupRequests = useMemo(
    () =>
      agencySignupRequests.slice().sort((a, b) => {
        const pendingDelta = Number(b.status === '審査待ち') - Number(a.status === '審査待ち')
        if (pendingDelta !== 0) return pendingDelta
        return Date.parse(b.createdAt) - Date.parse(a.createdAt)
      }),
    [agencySignupRequests],
  )
  const visibleAgencySignupRequests = useMemo(
    () =>
      showPendingRequestsOnly
        ? orderedAgencySignupRequests.filter((request) => request.status === '審査待ち')
        : orderedAgencySignupRequests,
    [orderedAgencySignupRequests, showPendingRequestsOnly],
  )

  if (!db || !me) return null

  if (me.role !== 'J-Navi管理者' && me.role !== '総代理店') {
    return (
      <Card title="代理店申請承認" subtitle="このページはJnavi運営または総代理店向けです">
        <div className="px-5 py-4 text-sm text-slate-600">権限がありません。</div>
      </Card>
    )
  }

  const handleApproveRequest = async (request: AgencySignupRequest) => {
    const confirmed = window.confirm(
      `${request.companyName} を承認します。代理店アカウント発行と承認案内メール送信まで続けて実行します。`,
    )
    if (!confirmed) return

    setActiveRequestAction(`${request.id}:approve`)

    const reviewResult = await actions.adminReviewAgencySignupRequest(request.id, '承認')
    if (!reviewResult.ok) {
      setActiveRequestAction(null)
      toast.show(reviewResult.message ?? '代理店申請を承認できませんでした', 'error')
      return
    }

    const issueResult = await actions.adminIssueAgencyAccount(request.id)
    if (!issueResult.ok) {
      setActiveRequestAction(null)
      toast.show(issueResult.message ?? '承認後の代理店アカウント発行に失敗しました', 'error')
      return
    }

    const notifyResult = await actions.adminSendAgencyApprovalNotification(request.id)
    setActiveRequestAction(null)
    if (!notifyResult.ok) {
      toast.show(notifyResult.message ?? '承認後の承認案内メール送信に失敗しました', 'error')
      return
    }

    const detail = [
      issueResult.email ?? request.email,
      issueResult.inviteCode ? `招待コード: ${issueResult.inviteCode}` : null,
    ]
      .filter(Boolean)
      .join(' / ')
    toast.show(detail ? `承認しました: ${detail}` : '代理店申請を承認しました', 'success')
  }

  const handleRejectRequest = async (request: AgencySignupRequest) => {
    const confirmed = window.confirm(`${request.companyName} を却下します。続行しますか？`)
    if (!confirmed) return

    setActiveRequestAction(`${request.id}:reject`)
    const result = await actions.adminReviewAgencySignupRequest(request.id, '却下')
    setActiveRequestAction(null)

    if (!result.ok) {
      toast.show(result.message ?? '代理店申請を却下できませんでした', 'error')
      return
    }

    toast.show('代理店申請を却下しました', 'success')
  }

  return (
    <Card
      title="代理店申請承認"
      subtitle={
        me.role === '総代理店'
          ? '自分宛の代理店申請を審査します。承認時はアカウント発行と承認案内メール送信まで続けて実行します'
          : '申請内容の確認と審査を行います。承認時はアカウント発行と承認案内メール送信まで続けて実行します'
      }
      right={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            className={`${actionButtonClass} border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}
            onClick={() => setShowPendingRequestsOnly((current) => !current)}
          >
            {showPendingRequestsOnly ? '全件表示' : '審査待ちのみ'}
          </button>
          <Badge tone={pendingAgencySignupRequestCount > 0 ? 'amber' : 'slate'}>
            審査待ち {pendingAgencySignupRequestCount} 件
          </Badge>
        </div>
      }
    >
      <div className="space-y-4 p-5">
        {visibleAgencySignupRequests.map((request) => {
          const issuedUser = request.issuedUserId ? findUser(db, request.issuedUserId) : undefined
          const requestSuperAgency = request.superAgencyId ? findUser(db, request.superAgencyId) : undefined
          const isBusy = activeRequestAction?.startsWith(`${request.id}:`) ?? false

          return (
            <div key={request.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-semibold text-slate-900">{request.companyName}</div>
                    <Badge tone={agencySignupStatusTone(request.status)}>{request.status}</Badge>
                  </div>
                  <div className="mt-1 text-sm text-slate-700">{request.contactName}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    受付日時: {formatDateTimeOrDash(request.createdAt)}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    所属総代理店: {requestSuperAgency?.name ?? (request.superAgencyId ? request.superAgencyId : '-')}
                  </div>
                </div>

                <div className="grid gap-2 text-xs text-slate-600 sm:text-right">
                  <div>{request.email}</div>
                  <div>電話番号: {request.phone ?? '-'}</div>
                  <div>
                    Web:{' '}
                    {request.website ? (
                      <a
                        className="text-blue-600 hover:underline"
                        href={request.website}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {request.website}
                      </a>
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-xs font-medium text-slate-500">審査情報</div>
                <div className="mt-1 space-y-1 text-xs text-slate-600">
                  <div>審査日時: {formatDateTimeOrDash(request.reviewedAt)}</div>
                  <div>
                    審査者:{' '}
                    {request.reviewedBy ? findUser(db, request.reviewedBy)?.name ?? request.reviewedBy : '-'}
                  </div>
                  <div>発行日時: {formatDateTimeOrDash(request.issuedAt)}</div>
                  <div>
                    発行先:{' '}
                    {issuedUser ? `${issuedUser.name} (${issuedUser.email})` : request.issuedUserId ?? '-'}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={`${actionButtonClass} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
                  disabled={isBusy || request.status !== '審査待ち'}
                  onClick={() => handleApproveRequest(request)}
                >
                  承認
                </button>
                <button
                  type="button"
                  className={`${actionButtonClass} border-slate-200 bg-white text-slate-700 hover:bg-slate-100`}
                  disabled={isBusy || request.status !== '審査待ち'}
                  onClick={() => handleRejectRequest(request)}
                >
                  却下
                </button>
              </div>
            </div>
          )
        })}

        {visibleAgencySignupRequests.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            {db.agencySignupRequests.length === 0
              ? '代理店申請はまだありません。'
              : showPendingRequestsOnly
                ? '審査待ちの代理店申請はありません。'
                : '表示できる代理店申請がありません。'}
          </div>
        ) : null}
      </div>
    </Card>
  )
}
